import { store } from '../../state/store';
import { api } from '../../api/client';
import type { SearchHit, Source, CordobaOri } from '../../api/types';
import { relativeTime } from '../../utils/hash';
import { loadNorma } from '../viewer/load';
import { BCRA_DOCS } from '../../data/bcra-docs';

const TIPOS_NACION: Array<{ value: string; label: string }> = [
  { value: '', label: 'Cualquier tipo' },
  { value: '1', label: 'Ley' },
  { value: '2', label: 'Decreto' },
  { value: '7', label: 'Decreto/Ley' },
  { value: '8', label: 'Decisión Administrativa' },
  { value: '3', label: 'Resolución' },
  { value: '4', label: 'Disposición' },
  { value: '12', label: 'Acordada' },
  { value: '11', label: 'Acta' },
  { value: '27', label: 'Actuación' },
  { value: '17', label: 'Acuerdo' },
  { value: '5', label: 'Circular' },
  { value: '6', label: 'Comunicación' },
  { value: '13', label: 'Comunicado' },
  { value: '20', label: 'Convenio' },
  { value: '14', label: 'Decisión' },
  { value: '15', label: 'Directiva' },
  { value: '10', label: 'Instrucción' },
  { value: '23', label: 'Interpretación' },
  { value: '24', label: 'Laudo' },
  { value: '18', label: 'Memorandum' },
  { value: '21', label: 'Misión' },
  { value: '16', label: 'Nota' },
  { value: '9', label: 'Nota Externa' },
  { value: '29', label: 'Ordenanza' },
  { value: '19', label: 'Protocolo' },
  { value: '28', label: 'Providencia' },
  { value: '22', label: 'Recomendación' },
];

// ─── Datos de normas frecuentes (extraídos de Leyes.md) ───
const NORMAS_FRECUENTES: Array<{
  cat: string;
  icon: string;
  items: Array<{ num: string; label: string; source: 'nacion' | 'cordoba'; id: string; ori?: 'L' | 'D' }>;
}> = [
  {
    cat: 'Córdoba', icon: 'ph ph-landmark',
    items: [
      { num: 'Const.', label: 'Constitución Provincial', source: 'cordoba', id: 'CP00', ori: 'L' },
      { num: '8435', label: 'L.O. del Poder Judicial', source: 'cordoba', id: '8C28C3EBA0AB9D9C032572340064377D', ori: 'L' },
      { num: '5350', label: 'Procedimiento Administrativo', source: 'cordoba', id: '0AFC8C301C28D4CF032584340069B8A3', ori: 'L' },
      { num: '7233', label: 'Estatuto Empleado Público', source: 'cordoba', id: 'B4E0D6549A13821503257BE1006695BB', ori: 'L' },
      { num: '8560', label: 'Ley de Tránsito Cba.', source: 'cordoba', id: 'DA71FAD708AFE1E7032589AD0049D381', ori: 'L' },
      { num: '10247', label: 'Defensa Consumidor Cba.', source: 'cordoba', id: 'D9E7D77C1B4F5BA903257DEB0076799E', ori: 'L' },
      { num: '10326', label: 'Código de Convivencia', source: 'cordoba', id: '268c4308c9994d6303258a160050dea6', ori: 'L' },
      { num: '10454', label: 'Catastro Territorial', source: 'cordoba', id: '4762597AB44FA44A0325865E00525C70', ori: 'L' },
      { num: '8465', label: 'C. Proc. Civil y Com.', source: 'cordoba', id: '19FD5340A2AA7E7003258A2000449696', ori: 'L' },
      { num: '10555', label: 'Procedimiento Oralidad Civil', source: 'cordoba', id: 'B7FFB3EE27EB8FD103258942004D744B', ori: 'L' },
      { num: '9459', label: 'Código Arancelario', source: 'cordoba', id: '9C737A40D78547FD03258C8400401375', ori: 'L' },
      { num: '10543', label: 'Ley de Mediación', source: 'cordoba', id: '18FE6C22A7C0A25B0325833200585E97', ori: 'L' },
      { num: '7987', label: 'C. Proc. del Trabajo', source: 'cordoba', id: '64647B903AEC46D0032586A9005B6969', ori: 'L' },
      { num: '7182', label: 'C. Contencioso Adm.', source: 'cordoba', id: 'EF9B8FD2619646E403257BE10057A1BE', ori: 'L' },
      { num: '8508', label: 'Amparo por mora', source: 'cordoba', id: 'ED22BBC6300F435F03257234006432DB', ori: 'L' },
      { num: '10618', label: 'Simplificación Adm.', source: 'cordoba', id: 'BE4B01D7CA5875C70325840000588906', ori: 'L' },
      { num: '10305', label: 'C. Proc. de Familia', source: 'cordoba', id: '18368E2F1061842303257ED80071D3C3', ori: 'L' },
      { num: 'CPP', label: 'C. Procesal Penal', source: 'cordoba', id: '34892CF23B741475032586BE00575E42', ori: 'L' },
      { num: '6006', label: 'C. Tributario Cba.', source: 'cordoba', id: '8517AEE2DE1FC2AA03258C3D0056BA50', ori: 'L' },
      { num: '11090', label: 'Ley Impositiva 2026', source: 'cordoba', id: 'B3BEF084722F5A2603258DA900560F4D', ori: 'L' }
    ],
  },
  {
    cat: 'Constitucional', icon: 'ph ph-scroll',
    items: [
      { num: 'CN', label: 'Constitución de la Nación', source: 'nacion', id: '804' },
      { num: '23.054', label: 'Conv. Americana de DDHH', source: 'nacion', id: '28152' },
    ],
  },
  {
    cat: 'Civil y Registral', icon: 'ph ph-users-three',
    items: [
      { num: 'CCyC', label: 'Código Civil y Comercial', source: 'nacion', id: '235975' },
      { num: '17.801', label: 'Reg. Prop. Inmueble', source: 'nacion', id: '53050' },
      { num: 'D-6582', label: 'Régimen del Automotor', source: 'nacion', id: '38822' },
      { num: '24.449', label: 'Ley Nacional de Tránsito', source: 'nacion', id: '818' },
      { num: '26.413', label: 'Reg. del Estado Civil', source: 'nacion', id: '145345' },
      { num: '26.589', label: 'Mediación Prejudicial', source: 'nacion', id: '166999' },
      { num: '24.240', label: 'Defensa del Consumidor', source: 'nacion', id: '638' },
      { num: '26.485', label: 'Protección Integral Mujeres', source: 'nacion', id: '152155' },
      { num: '11.723', label: 'Propiedad Intelectual', source: 'nacion', id: '42755' },
      { num: '22.362', label: 'Marcas y Designaciones', source: 'nacion', id: '18803' },
      { num: '25.326', label: 'Protección Datos Personales', source: 'nacion', id: '64790' },
      { num: 'D-1558', label: 'Regl. Datos Personales', source: 'nacion', id: '70368' },
      { num: '25.506', label: 'Firma Digital', source: 'nacion', id: '70749' },
      { num: '26.657', label: 'Salud Mental', source: 'nacion', id: '175977' },
      { num: '26.529', label: 'Derechos del Paciente', source: 'nacion', id: '160432' },
    ],
  },
  {
    cat: 'Corporativo / Financiero', icon: 'ph ph-bank',
    items: [
      { num: '19.550', label: 'Ley Gral. de Sociedades', source: 'nacion', id: '25553' },
      { num: '27.349', label: 'Capital Emprendedor (SAS)', source: 'nacion', id: '273567' },
      { num: '20.337', label: 'Ley de Cooperativas', source: 'nacion', id: '18462' },
      { num: '20.321', label: 'Ley de Mutuales', source: 'nacion', id: '25392' },
      { num: '24.522', label: 'Concursos y Quiebras', source: 'nacion', id: '25379' },
      { num: '27.442', label: 'Defensa de la Competencia', source: 'nacion', id: '310241' },
      { num: 'D-274', label: 'Lealtad Comercial', source: 'nacion', id: '322236' },
      { num: '24.467', label: 'PyMES', source: 'nacion', id: '15932' },
      { num: '24.144', label: 'Carta Orgánica BCRA', source: 'nacion', id: '66194' },
      { num: '21.526', label: 'Entidades Financieras', source: 'nacion', id: '16071' },
      { num: '26.831', label: 'Mercado de Capitales', source: 'nacion', id: '206592' },
      { num: '23.576', label: 'Obligaciones Negociables', source: 'nacion', id: '20643' },
      { num: '24.083', label: 'Fondos Comunes de Inversión', source: 'nacion', id: '482' },
      { num: 'D-15348', label: 'Prenda con Registro', source: 'nacion', id: '31308' },
      { num: '9.643', label: 'Warrants', source: 'nacion', id: '37048' },
      { num: 'D-5965', label: 'Letras de Cambio y Pagarés', source: 'nacion', id: '69687' },
      { num: '24.452', label: 'Cheques', source: 'nacion', id: '14733' },
      { num: '25.065', label: 'Tarjetas de Crédito', source: 'nacion', id: '55556' },
      { num: '17.418', label: 'Ley de Seguros', source: 'nacion', id: '39520' },
    ],
  },
  {
    cat: 'Administrativo', icon: 'ph ph-buildings',
    items: [
      { num: '19.549', label: 'Procedimientos Administrativos', source: 'nacion', id: '22363' },
      { num: 'D-1759', label: 'Regl. Proc. Administrativo', source: 'nacion', id: '21715' },
      { num: '16.986', label: 'Acción de Amparo', source: 'nacion', id: '46871' },
      { num: '13.064', label: 'Obras Públicas', source: 'nacion', id: '38542' },
      { num: '26.944', label: 'Responsabilidad del Estado', source: 'nacion', id: '233216' },
      { num: '24.937', label: 'Consejo de la Magistratura', source: 'nacion', id: '48356' },
      { num: '27.275', label: 'Acceso a la Info. Pública', source: 'nacion', id: '265949' },
      { num: 'D-971', label: 'Silencio Positivo', source: 'nacion', id: '405911' },
      { num: '19.945', label: 'Código Electoral Nacional', source: 'nacion', id: '19442' },
    ],
  },
  {
    cat: 'Laboral y Previsional', icon: 'ph ph-briefcase',
    items: [
      { num: '20.744', label: 'Contrato de Trabajo', source: 'nacion', id: '25552' },
      { num: '14.250', label: 'Convenciones Colectivas', source: 'nacion', id: '46379' },
      { num: '24.013', label: 'Ley Nacional de Empleo', source: 'nacion', id: '412' },
      { num: '27.555', label: 'Teletrabajo', source: 'nacion', id: '341017' },
      { num: '27.802', label: 'Modernización Laboral', source: 'nacion', id: '423680' },
      { num: 'D-407', label: 'Regl. Modernización Laboral', source: 'nacion', id: '426270' },
      { num: 'D-408', label: 'Fondo de Asistencia Laboral (FAL)', source: 'nacion', id: '426272' },
      { num: 'D-409', label: 'Régimen de Empleo Registrado', source: 'nacion', id: '426271' },
      { num: '27.742', label: 'Ley de Bases', source: 'nacion', id: '401266' },
      { num: '11.544', label: 'Jornada de Trabajo', source: 'nacion', id: '63368' },
      { num: '24.557', label: 'Riesgos del Trabajo', source: 'nacion', id: '27971' },
      { num: '23.551', label: 'Asociaciones Sindicales', source: 'nacion', id: '20993' },
      { num: '24.241', label: 'Jubilaciones y Pensiones', source: 'nacion', id: '639' },
      { num: '26.425', label: 'SIPA', source: 'nacion', id: '148141' },
      { num: '19.032', label: 'INSSJP (PAMI)', source: 'nacion', id: '16081' },
      { num: '23.660', label: 'Obras Sociales', source: 'nacion', id: '62' },
      { num: '23.661', label: 'Seguro de Salud', source: 'nacion', id: '63' },
      { num: '24.901', label: 'Prestaciones en Discapacidad', source: 'nacion', id: '47677' },
      { num: '26.844', label: 'Personal Casas Part.', source: 'nacion', id: '210489' },
    ],
  },
  {
    cat: 'Tributario / Aduanero', icon: 'ph ph-receipt',
    items: [
      { num: '11.683', label: 'Proc. Tributario', source: 'nacion', id: '18771' },
      { num: '20.628', label: 'Ganancias', source: 'nacion', id: '332890' },
      { num: 'IVA', label: 'Impuesto al Valor Agregado', source: 'nacion', id: '42701' },
      { num: '22.415', label: 'Código Aduanero', source: 'nacion', id: '16536' },
      { num: '24.674', label: 'Impuestos Internos', source: 'nacion', id: '38621' },
      { num: '24.977', label: 'Monotributo', source: 'nacion', id: '51609' },
      { num: '23.966', label: 'Bienes Personales', source: 'nacion', id: '365' },
    ],
  },
  {
    cat: 'Penal', icon: 'ph ph-scales',
    items: [
      { num: 'CP', label: 'Código Penal', source: 'nacion', id: '16546' },
      { num: '24.660', label: 'Ejecución de la Pena', source: 'nacion', id: '37872' },
      { num: '27.401', label: 'Resp. Penal Empresaria', source: 'nacion', id: '296846' },
      { num: '27.430', label: 'Régimen Penal Tributario', source: 'nacion', id: '305262' },
      { num: '19.359', label: 'Régimen Penal Cambiario', source: 'nacion', id: '28133' },
      { num: '25.246', label: 'Lavado de Activos', source: 'nacion', id: '62977' },
      { num: '23.737', label: 'Estupefacientes', source: 'nacion', id: '138' },
    ],
  },
  {
    cat: 'Ambiental y Recursos', icon: 'ph ph-leaf',
    items: [
      { num: '25.675', label: 'Ley Gral. del Ambiente', source: 'nacion', id: '79980' },
      { num: '17.319', label: 'Ley de Hidrocarburos', source: 'nacion', id: '16078' },
      { num: '1.919', label: 'Código Minero', source: 'nacion', id: '43797' },
      { num: '20.094', label: 'Ley de Navegación', source: 'nacion', id: '43550' },
      { num: '17.285', label: 'Código Aeronáutico', source: 'nacion', id: '24963' },
    ],
  },
  {
    cat: 'Procesal Federal', icon: 'ph ph-file-text',
    items: [
      { num: 'CPCCN', label: 'C. Proc. Civil y Com. Nac.', source: 'nacion', id: '16547' },
      { num: 'CPPF', label: 'C. Proc. Penal Federal', source: 'nacion', id: '319681' },
    ],
  },
];

export function renderSearchPanel(root: HTMLElement): void {
  root.innerHTML = `
    <header class="search-header">
      <div class="brand" role="button" title="Ir al inicio" style="cursor:pointer">
        <span class="brand-mark">§</span>
        <span class="brand-name">LexCore</span>
      </div>
      <button class="icon-btn theme-toggle md-state-layer" aria-label="Cambiar tema" title="Cambiar tema">
        <i class="ph ph-moon-stars"></i>
      </button>
    </header>
    <nav class="tabs" role="tablist">
      <button class="tab md-state-layer" data-tab="nacion" role="tab" aria-selected="true">Nación</button>
      <button class="tab md-state-layer" data-tab="cordoba" role="tab" aria-selected="false">Córdoba</button>
      <button class="tab md-state-layer" data-tab="theme" role="tab" aria-selected="false">Temática</button>
    </nav>
    <div data-search-area>
    <div class="search-entry">
      <div class="search-input-wrap">
        <span class="search-icon"><i class="ph ph-magnifying-glass"></i></span>
        <input class="search-input" type="search" placeholder="Buscar (Ctrl+K)" autocomplete="off" spellcheck="false" />
        <kbd class="search-kbd">Ctrl+K</kbd>
      </div>
      <button class="btn filled search-submit md-state-layer" type="button">Buscar</button>
    </div>
    <div class="filters" data-filters="nacion">
      <select class="chip-select" data-filter="tipo" aria-label="Tipo">
        ${TIPOS_NACION.map((t) => `<option value="${t.value}">${t.label}</option>`).join('')}
      </select>
      <input class="chip-input" data-filter="anio" type="number" min="1853" max="${new Date().getFullYear()}" placeholder="Año" />
    </div>
    <div class="filters hidden" data-filters="cordoba">
      <select class="chip-select" data-filter="ori" aria-label="Origen">
        <option value="L">Leyes</option>
        <option value="D">Decretos</option>
      </select>
      <input class="chip-input" data-filter="anio-cba" type="number" min="1820" max="${new Date().getFullYear()}" placeholder="Año" />
    </div>
    <div class="results" data-results aria-live="polite"><div class="empty">Escribí un número, año o palabras clave.</div></div>
    <details class="recents" open>
      <summary>Recientes</summary>
      <div class="recents-body">
        <ul class="recents-list" data-recents></ul>
      </div>
    </details>
    ${renderNormasFrecuentes()}
    ${renderBcraSection()}
    <footer class="search-footer" data-footer></footer>
    </div><!-- /data-search-area -->
  `;

  const input = root.querySelector<HTMLInputElement>('.search-input')!;
  const resultsEl = root.querySelector<HTMLDivElement>('[data-results]')!;
  const recentsEl = root.querySelector<HTMLUListElement>('[data-recents]')!;
  const footerEl = root.querySelector<HTMLElement>('[data-footer]')!;
  const bcraDetails = root.querySelector<HTMLDetailsElement>('[data-bcra]')!;
  const bcraListEl = root.querySelector<HTMLDivElement>('[data-bcra-list]')!;
  const themeBtn = root.querySelector<HTMLButtonElement>('.theme-toggle')!;
  const submitBtn = root.querySelector<HTMLButtonElement>('.search-submit')!;
  const brand = root.querySelector<HTMLElement>('.brand')!;
  brand.addEventListener('click', () => {
    store.currentNorma.set(null);
    store.drawerOpen.set(false);
  });

  // Wedge Global
  const wedge = document.getElementById('global-search-wedge');
  if (wedge) {
    wedge.addEventListener('click', () => {
      store.drawerOpen.set(!store.drawerOpen.get());
    });
    // Ocultar cuña si el panel está abierto para no tapar nada
    store.drawerOpen.subscribe(open => {
      wedge.classList.toggle('hidden', open);
    });
  }

  const tabs = root.querySelectorAll<HTMLButtonElement>('.tab');
  const filtersNacion = root.querySelector<HTMLDivElement>('[data-filters="nacion"]')!;
  const filtersCordoba = root.querySelector<HTMLDivElement>('[data-filters="cordoba"]')!;
  const tipoSelect = filtersNacion.querySelector<HTMLSelectElement>('[data-filter="tipo"]')!;

  themeBtn.addEventListener('click', () => {
    store.setTheme(store.theme.get() === 'dark' ? 'light' : 'dark');
  });

  const updatePlaceholder = (t: Source) => {
    if (t === 'nacion') {
      input.placeholder = 'Nro de Ley / Decreto... (Ctrl+K)';
    } else if (t === 'cordoba') {
      input.placeholder = 'Nro de Ley o Año... (Ctrl+K)';
    } else if (t === 'theme') {
      input.placeholder = 'Concepto (ej. entidades financieras)... (Ctrl+K)';
    } else {
      input.placeholder = 'Buscar (Ctrl+K)';
    }
  };

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const t = tab.dataset.tab as Source;
      tabs.forEach((b) => b.setAttribute('aria-selected', String(b === tab)));

      store.activeTab.set(t as Source);
      filtersNacion.classList.toggle('hidden', t !== 'nacion');
      filtersCordoba.classList.toggle('hidden', t !== 'cordoba');
      updatePlaceholder(t);
      runSearch();
    });
  });

  // Inicializar placeholder según pestaña activa
  updatePlaceholder(store.activeTab.get());

  let abort: AbortController | null = null;
  async function runSearch(): Promise<void> {
    abort?.abort();
    abort = new AbortController();
    const signal = abort.signal;
    const q = store.query.get();
    const tab = store.activeTab.get();
    store.searching.set(true);
    store.searchError.set(null);
    try {
      if (tab === 'nacion') {
        const qTrimmed = q.trim();
        const isNumericOnly = qTrimmed.length > 0 && /^[\d.\s/-]+$/.test(qTrimmed) && /\d/.test(qTrimmed);
        let tipo = tipoSelect.value || undefined;
        if (qTrimmed && !isNumericOnly) {
          store.searchError.set('Infoleg: ingrese un numero de norma. Si no selecciona tipo, se asume Ley.');
          store.searchResults.set([]);
          return;
        }
        if (isNumericOnly && !tipo) {
          tipo = '1'; // Ley
          tipoSelect.value = '1';
        }
        const anioRaw = (filtersNacion.querySelector('[data-filter="anio"]') as HTMLInputElement).value;
        const anio = anioRaw ? Number(anioRaw) : undefined;
        if (!q && !anio) {
          store.searchResults.set([]);
          return;
        }
        const r = await api.searchInfoleg({ q, tipo, anio, signal });
        if (signal.aborted) return;
        store.searchResults.set(r.hits);
        if (r.indexUpdatedAt) store.indexUpdatedAt.set(r.indexUpdatedAt);

        // Auto-abrir si hay exactamente 1 resultado
        if (r.hits.length === 1) {
          const h = r.hits[0];
          void loadNorma(h.source, h.id, h.ori, false, h.titulo);
        }
      } else if (tab === 'cordoba') {
        const ori = (filtersCordoba.querySelector('[data-filter="ori"]') as HTMLSelectElement).value as CordobaOri;
        const anioRaw = (filtersCordoba.querySelector('[data-filter="anio-cba"]') as HTMLInputElement).value;
        const anio = anioRaw ? Number(anioRaw) : undefined;
        if (!q && !anio) {
          store.searchResults.set([]);
          return;
        }
        const r = await api.searchCordoba(ori, q || undefined, anio, signal);
        if (signal.aborted) return;
        store.searchResults.set(r.hits);

        // Auto-abrir si hay exactamente 1 resultado
        if (r.hits.length === 1) {
          const h = r.hits[0];
          void loadNorma(h.source, h.id, h.ori, false, h.titulo);
        }
      } else if (tab === 'theme') {
        if (!q.trim()) {
          store.searchResults.set([]);
          return;
        }
        const r = await api.searchTheme(q, signal);
        if (signal.aborted) return;
        store.searchResults.set(r.hits);

        // Auto-abrir si hay exactamente 1 resultado
        if (r.hits.length === 1) {
          const h = r.hits[0];
          void loadNorma(h.source, h.id, h.ori, false, h.titulo);
        }
      }
    } catch (e) {
      if (signal.aborted || (e as Error).name === 'AbortError') return;
      const tab = store.activeTab.get();
      if (tab === 'theme') {
        const googleQuery = `(site:servicios.infoleg.gob.ar/infolegInternet/verNorma.do OR site:web2.cba.gov.ar/web/leyes.nsf) ${q}`;
        const googleLink = `https://www.google.com/search?q=${encodeURIComponent(googleQuery)}`;
        store.searchError.set(`No se pudo completar la búsqueda temática por red.<br/><br/><a class="btn outlined" href="${googleLink}" target="_blank" rel="noopener noreferrer" style="margin-top: 12px; display: inline-flex; align-items: center; gap: 8px;"><i class="ph ph-google-logo"></i> Buscar en Google original</a>`);
      } else {
        store.searchError.set((e as Error).message);
      }
      store.searchResults.set([]);
    } finally {
      if (!signal.aborted) {
        store.searching.set(false);
      }
    }
  }

  input.addEventListener('input', () => { store.query.set(input.value); });
  root.querySelector('.brand')?.addEventListener('click', () => {
    store.currentNorma.set(null);
    history.replaceState(null, '', '#/');
  });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); void runSearch(); }
  });
  submitBtn.addEventListener('click', () => void runSearch());
  filtersNacion.addEventListener('change', runSearch);
  filtersCordoba.addEventListener('change', runSearch);

  // Suscripciones de seguridad al estado global (removida la suscripción manual duplicada)
  
  store.currentNorma?.subscribe?.(() => {
    if (store.searchResults?.get) renderHits(resultsEl, store.searchResults.get());
  });
  
  store.searching?.subscribe?.((searching) => {
    resultsEl.classList.toggle('searching', searching);
    if (searching) {
      resultsEl.innerHTML = '<div class="searching-state"><span class="spinner"></span> Buscando...</div>';
    }
  });
  
  store.searchError?.subscribe?.((err) => {
    if (err) resultsEl.innerHTML = `<div class="error">${err}</div>`;
  });
  
  store.recents?.subscribe?.(() => renderRecents(recentsEl));
  
  store.indexUpdatedAt?.subscribe?.((ts) => {
    footerEl.textContent = ts ? `Índice Infoleg actualizado ${relativeTime(ts)}` : '';
  });

  // ─── Acordeón secciones principales ─────────────────────────────────────
  // Al abrir Recientes, NormasFrecuentes o BCRA, las demás se cierran.
  const topSections = Array.from(
    root.querySelectorAll<HTMLDetailsElement>('.recents, .normas-frecuentes, .bcra-ordenados')
  );

  topSections.forEach(section => {
    section.addEventListener('toggle', () => {
      if (!section.open) return;
      topSections.forEach(other => {
        if (other !== section && other.open) other.open = false;
      });
    });
  });

  store.searchResults?.subscribe?.((hits) => {
    renderHits(resultsEl, hits);
    if (hits.length > 0) {
      // Auto-colapsar secciones para dar espacio a los resultados
      topSections.forEach(s => s.open = false);
    }
  });

  store.searching?.subscribe?.((loading) => {
    if (loading) {
      resultsEl.innerHTML = `<div class="searching-state"><div class="spinner"></div>Buscando...</div>`;
      topSections.forEach(s => s.open = false);
    }
  });

  let bcraLoaded = false;
  bcraDetails.addEventListener('toggle', () => {
    if (!bcraDetails.open || bcraLoaded) return;
    bcraLoaded = true;
    loadBcraDocs(bcraListEl);
  });

  // Normas frecuentes — event delegation + comportamiento acordeón
  root.querySelector('.nf-scroll')?.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.nf-link');
    if (!btn) {
      // Comprobar si se hizo click en el summary de un nf-cat (acordeón)
      const summary = (e.target as HTMLElement).closest<HTMLElement>('summary');
      const cat = summary?.closest<HTMLDetailsElement>('.nf-cat');
      if (cat && !cat.open) {
        // Se va a abrir: cerrar todos los demás primero
        root.querySelectorAll<HTMLDetailsElement>('.nf-cat[open]').forEach(d => {
          if (d !== cat) d.open = false;
        });
      }
      return;
    }
    const { source, id, ori, titulo } = btn.dataset;
    if (!source || !id) return;
    void loadNorma(source as Source, id, ori as 'L' | 'D' | undefined, false, titulo);
  });

  // expose focus for shortcut
  (window as unknown as { focusSearch: () => void }).focusSearch = () => input.focus();
}

function renderNormasFrecuentes(): string {
  const cats = NORMAS_FRECUENTES.map(({ cat, icon, items }) => `
    <details class="nf-cat">
      <summary class="md-state-layer">
        <span><i class="${icon}"></i> ${escapeHtml(cat)}</span>
        <i class="ph ph-caret-right nf-arrow"></i>
      </summary>
      <ul class="nf-list">
        ${items.map((item) => `
          <li>
            <button class="nf-link"
              data-source="${item.source}"
              data-id="${escapeHtml(item.id)}"
              data-titulo="${escapeHtml(item.label)}"
              ${item.ori ? `data-ori="${item.ori}"` : ''}
              title="${escapeHtml(item.label)}"
            >
              <span class="nf-num">${escapeHtml(item.num)}</span>
              <span class="nf-name">${escapeHtml(item.label)}</span>
            </button>
          </li>`).join('')}
      </ul>
    </details>
  `).join('');

  return `
    <details class="normas-frecuentes">
      <summary>Normas frecuentes</summary>
      <div class="nf-scroll">${cats}</div>
    </details>
  `;
}

function renderBcraSection(): string {
  return `
    <details class="bcra-ordenados" data-bcra>
      <summary>Normativa BCRA (PDF)</summary>
      <div class="bcra-body" data-bcra-list>
        <p class="bcra-loading">Abrí esta sección para cargar los textos ordenados.</p>
      </div>
    </details>
  `;
}

function loadBcraDocs(root: HTMLElement): void {
  root.innerHTML = renderBcraGroup(BCRA_DOCS);
  
  // Adjuntar listeners a los nuevos links
  root.querySelectorAll<HTMLAnchorElement>('.bcra-link').forEach(link => {
    link.addEventListener('click', (e) => {
      // Si el usuario hace Ctrl+Click o botón medio, dejamos que el navegador abra el PDF directamente
      if (e.ctrlKey || e.metaKey || e.button === 1) return;
      
      e.preventDefault();
      const url = link.dataset.url!;
      const title = link.dataset.title;
      void loadNorma('bcra', url, undefined, false, title);
    });
  });
}

function renderBcraGroup(docs: typeof BCRA_DOCS): string {
  return `
    <ul class="bcra-list">
      ${docs.map((d) => `
        <li>
          <a href="${escapeHtml(d.url)}" class="bcra-link md-state-layer" 
             data-url="${escapeHtml(d.url)}" data-title="${escapeHtml(d.title)}">
            <i class="ph ph-file-pdf"></i>
            <span>${escapeHtml(d.title)}</span>
            <i class="ph ph-arrow-square-in int-icon"></i>
          </a>
        </li>`).join('')}
    </ul>
  `;
}

function renderHits(root: HTMLElement, hits: SearchHit[]): void {
  if (hits.length === 0) {
    const q = store.query.get().trim();
    if (!q) {
      root.innerHTML = `<div class="empty">Escribí un número, año o palabras clave.</div>`;
    } else {
      const activeTab = store.activeTab.get();
      if (activeTab === 'theme') {
        const googleQuery = `site:servicios.infoleg.gob.ar/infolegInternet/verNorma.do OR site:web2.cba.gov.ar/web/leyes.nsf ${q}`;
        const googleLink = `https://www.google.com/search?q=${encodeURIComponent(googleQuery)}`;
        root.innerHTML = `
          <div class="empty" style="text-align: center; padding: 24px 16px;">
            <p>No se encontraron resultados conceptuales.</p>
            <a class="btn outlined" href="${googleLink}" target="_blank" rel="noopener noreferrer" style="margin-top: 12px; display: inline-flex; align-items: center; gap: 8px;">
              <i class="ph ph-google-logo"></i> Buscar en Google original
            </a>
          </div>
        `;
      } else {
        root.innerHTML = `<div class="empty">No se encontraron resultados para "${escapeHtml(q)}".</div>`;
      }
    }
    return;
  }
  const current = store.currentNorma.get();
  root.innerHTML = hits
    .map(
      (h) => `
      <button class="result md-state-layer ${isActiveHit(current, h) ? 'active' : ''}" data-source="${h.source}" data-id="${h.id}" data-ori="${h.ori ?? ''}" data-titulo="${escapeHtml(h.titulo)}">
        <div class="result-line1">
          <span class="badge">${escapeHtml(h.tipo || '—')}</span>
          <span class="result-numero">N° ${escapeHtml(h.numero || '—')}</span>
          ${h.anio ? `<span class="result-anio">${h.anio}</span>` : ''}
        </div>
        <div class="result-titulo">${escapeHtml(h.titulo || 'Sin título')}</div>
      </button>`,
    )
    .join('');

  root.querySelectorAll<HTMLButtonElement>('.result').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.id!;
      const source = btn.dataset.source as Source;
      const ori = btn.dataset.ori === 'L' || btn.dataset.ori === 'D' ? btn.dataset.ori : undefined;
      const titulo = btn.dataset.titulo;
      void loadNorma(source, id, ori, false, titulo);
    });
  });
}

function isActiveHit(current: { source: Source; id: string; ori?: CordobaOri } | null, hit: SearchHit): boolean {
  if (!current) return false;
  if (current.source !== hit.source || current.id !== hit.id) return false;
  if (hit.source !== 'cordoba') return true;
  return (current.ori ?? '') === (hit.ori ?? '');
}

function renderRecents(root: HTMLElement): void {
  const items = store.recents.get();
  if (items.length === 0) {
    root.innerHTML = `<li class="empty">Aún no consultaste normas.</li>`;
    return;
  }
  const sorted = [...items].sort((a, b) => Number(!!b.favorite) - Number(!!a.favorite) || b.visitedAt - a.visitedAt);
  root.innerHTML = sorted
    .slice(0, 20)
    .map(
      (r) => `
      <li>
        <button class="recent md-state-layer" data-source="${r.source}" data-id="${escapeHtml(r.id)}" data-ori="${r.ori ?? ''}">
          <span class="star ${r.favorite ? 'on' : ''}" data-fav title="${r.favorite ? 'Quitar de favoritos' : 'Marcar como favorito'}">
            <i class="ph-bold ${r.favorite ? 'ph-star-fill' : 'ph-star'}"></i>
          </span>
          <span class="recent-text">
            <strong>${escapeHtml(r.tipo)}${r.numero ? ` N° ${escapeHtml(r.numero)}` : ''}</strong>
            <small>${r.titulo ? escapeHtml(r.titulo) : 'Sin título'}</small>
          </span>
        </button>
      </li>`,
    )
    .join('');

  root.querySelectorAll<HTMLButtonElement>('.recent').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const source = btn.dataset.source as Source;
      const id = btn.dataset.id!;
      if (target.closest('[data-fav]')) {
        e.stopPropagation();
        store.toggleFavorite(source, id);
        return;
      }
      const item = sorted.find(x => x.source === source && x.id === id);
      void loadNorma(source, id, btn.dataset.ori ? (btn.dataset.ori as 'L' | 'D') : undefined, false, item?.titulo);
    });
  });

}

function escapeHtml(s: unknown): string {
  const text = String(s ?? '');
  return text.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
}
